import { IBrandPermission } from "@shared/lib/types/permissions";
import { Col, Form, Row, Typography } from "@shared/ui";
import { Checkbox } from "@shared/ui/Checkbox";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { Flex, Space } from "antd";
import { useEffect, useMemo } from "react";
import styles from "./styles.module.scss";

const { Title } = Typography;

const isViewPermission = (permission: IBrandPermission): boolean =>
  permission.name.startsWith("view-");

export const PermissionGroup = ({
  groupName,
  allPermissions,
}: {
  groupName: string;
  allPermissions: IBrandPermission[];
}) => {
  const group = useMemo(() => {
    return allPermissions.filter((item) => item.category === groupName);
  }, [allPermissions]);
  const form = Form.useFormInstance();
  const permissions: string[] = Form.useWatch("permissions", form) || [];

  const allSelected = group.every((item) => permissions.includes(item.name));
  const someSelected = group.some((item) => permissions.includes(item.name));

  const handleSelectAll = () => {
    const groupIds = group.map((i) => i.name);
    let newPermissions;
    if (allSelected) {
      newPermissions = permissions.filter((p: string) => !groupIds.includes(p));
    } else {
      newPermissions = [...new Set([...permissions, ...groupIds])];
    }
    form.setFieldsValue({ permissions: newPermissions });
  };

  const handleToggle = (permissionName: string) => {
    const currentPermissions = form.getFieldValue("permissions") || [];
    let newPermissions;
    if (currentPermissions.includes(permissionName)) {
      newPermissions = currentPermissions.filter(
        (p: string) => p !== permissionName
      );
    } else if (permissionName) {
      newPermissions = [...currentPermissions, permissionName];
    }

    form.setFieldsValue({ permissions: newPermissions || [] });
  };

  useEffect(() => {
    group.forEach((item) => {
      const checkEnabled = allPermissions
        ?.filter((val) => permissions.includes(val.name))
        ?.some((val) => val.hardSelect?.includes(item.name));
      if (checkEnabled && !permissions.includes(item.name)) {
        handleToggle(item.name);
      }
    }); // for each element of group, check if selected elements have hardSelect list, so enable the hard select element
  }, [permissions, group, allPermissions]);

  return (
    <div className={styles.permissionGroup}>
      <div
        className={styles.groupHeader}
        onClick={(e) => {
          handleSelectAll();
          e.stopPropagation();
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={5} style={{ textTransform: "capitalize" }}>
              {groupName}
            </Title>
          </Col>
          <Col>
            <Space className={styles.selectAll}>
              <span>
                {allSelected
                  ? "All Selected"
                  : someSelected
                    ? "Partially Selected"
                    : "Select All"}
              </span>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
              />
            </Space>
          </Col>
        </Row>
      </div>

      {/* <Space direction="vertical" size={"small"} style={{ width: "100%" }}> */}
      <Flex vertical gap={0}>
        {group.map((item) => {
          const checkEnabled = allPermissions
            ?.filter((val) => permissions.includes(val.name))
            ?.filter((val) => val.hardSelect?.includes(item.name));

          return (
            <DisabledPopover
              isDisabled={checkEnabled?.length > 0}
              key={item.name}
              content={
                <Flex vertical gap={5}>
                  Permission selected by default due to the following enabled
                  permissions:
                  <ul style={{ paddingLeft: 20 }}>
                    {checkEnabled?.map((val) => (
                      <li key={val.name}>{val.displayName}</li>
                    ))}
                  </ul>
                </Flex>
              }
            >
              <Checkbox
                value={item.name}
                key={item.name}
                className={styles.permissionItem}
                checked={permissions.includes(item.name)}
                disabled={checkEnabled?.length > 0}
                onChange={() => handleToggle(item.name)}
              >
                {item.displayName}
              </Checkbox>
            </DisabledPopover>
          );
        })}
      </Flex>
      {/* </Space> */}
    </div>
  );
};
